import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getConfig } from '@edx/frontend-platform';
import { Spinner, Button } from '@openedx/paragon';
import { RiArrowLeftSLine } from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { usePaymentGateway } from '@blend-ed/blendxplatform/hooks';
import { getFeatureToggle } from '@blend-ed/blendxplatform/utils';

import { useCourseDetail } from '../../../gql/hooks/useCourseDetail';
import { useCourseReviews } from '../../../gql/hooks/useCourseReviews';
import { useCart } from '../../../gql/hooks/useCart';
import { useIsEnrolledInCourse } from '../../../gql/hooks/useIsEnrolledInCourse';
import { useCoursesEffort } from '../useCoursesEffort';
import { useCoursesIsNew } from '../useCoursesIsNew';
import { formatMinutes } from '../courseDataParser';
import { useCourseOutline } from './useCourseOutline';
import { useProductSubscription } from '../subscriptions/useProductSubscription';
import { useCourseOverview } from './useCourseOverview';
import {
  CourseHeader,
  AboutSection,
  CourseOutline,
  CourseReviews,
  PurchasePanel,
} from './components';
import messages from './components/messages';

import './index.scss';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1200&q=60';

const CourseDetail = () => {
  const intl = useIntl();
  const { courseId: rawCourseId } = useParams();
  const courseId = decodeURIComponent(rawCourseId || '');
  const navigate = useNavigate();

  const { course, loading: courseLoading, error: courseError } = useCourseDetail(courseId);
  const { outline } = useCourseOutline(courseId);
  const { overview } = useCourseOverview(courseId);
  const { effortByCourseId } = useCoursesEffort(courseId ? [courseId] : []);
  const { isNewByCourseId } = useCoursesIsNew(courseId ? [courseId] : []);
  const { reviews, distribution } = useCourseReviews(course?.id);
  const { isEnrolled, enrolledMode } = useIsEnrolledInCourse(courseId);
  const {
    coursesInCart,
    coursesInWishlist,
    cartItemByCourseId,
    setCourseCartState,
    updateCartMode,
    pendingTarget,
    refetch: refetchCart,
  } = useCart();
  const { gateway } = usePaymentGateway();
  const featuresToggled = getFeatureToggle();
  const subscriptionsEnabled = !featuresToggled.loading && featuresToggled.features_subscriptions === true;
  const subscription = useProductSubscription({
    productType: 'course', courseId, enabled: subscriptionsEnabled,
  });
  const paymentsEnabled = !featuresToggled.loading && featuresToggled.features_payments === true;
  // Cart on by default; only an explicit `features_cart === false` disables it.
  const cartEnabled = featuresToggled.features_cart !== false;

  const contentId = course?.id;
  const cartItem = contentId ? cartItemByCourseId.get(contentId) : null;
  const isInCart = contentId ? coursesInCart.has(contentId) : false;
  const isInWishlist = contentId ? coursesInWishlist.has(contentId) : false;

  const handleToggleCart = (_contentId, mode) => {
    if (!contentId) {
      return;
    }
    setCourseCartState(contentId, 'cart', mode);
  };
  const handleToggleWishlist = () => {
    if (!contentId) {
      return;
    }
    setCourseCartState(contentId, 'wishlist');
  };
  const handleChangeMode = (mode) => {
    if (!contentId) {
      return;
    }
    updateCartMode(contentId, mode);
  };
  const handleEnrollNow = async (_contentId, mode) => {
    if (!contentId) {
      return;
    }
    if (!isInCart) {
      await setCourseCartState(contentId, 'cart', mode);
    } else if (mode && cartItem?.mode !== mode) {
      await updateCartMode(contentId, mode);
    }
    const { data: fresh } = await refetchCart();
    const row = (fresh?.cart || []).find(
      (r) => r.courseId === contentId
        && (r.status === 'draft' || r.status === 'pending'),
    );
    if (!row || !gateway) {
      return;
    }
    const lmsBase = (getConfig().LMS_BASE_URL || '').replace(/\/$/, '');
    window.location.href = `${lmsBase}/blendxapi/payment/${gateway}/create_payment_intent/?cart_ids=${row.id}`;
  };

  if (courseLoading) {
    return (
      <div className="course-detail__state">
        <Spinner animation="border" screenReaderText={intl.formatMessage(messages.loadingCourse)} />
      </div>
    );
  }

  if (courseError || !course) {
    return (
      <div className="course-detail__state course-detail__state--error">
        {intl.formatMessage(messages.courseLoadError)}
      </div>
    );
  }

  const details = course.details || {};
  const imageUrl = details.courseImageUrl
    ? `${getConfig().LMS_BASE_URL}${details.courseImageUrl}`
    : FALLBACK_IMAGE;
  const totalMinutes = effortByCourseId[courseId]?.total_minutes;
  const duration = formatMinutes(totalMinutes);

  return (
    <div className="course-detail">
      <Button
        variant="tertiary"
        size="sm"
        className="course-detail__back"
        iconBefore={RiArrowLeftSLine}
        onClick={() => navigate(-1)}
      >
        {intl.formatMessage(messages.goBack)}
      </Button>

      <div className="course-detail__layout">
        <div className="course-detail__main">
          <CourseHeader
            title={details.displayName || intl.formatMessage(messages.untitledCourse)}
            duration={duration}
            rating={distribution.total > 0 ? distribution.avg : null}
            reviewCount={distribution.total}
            imageUrl={imageUrl}
          />

          <AboutSection html={overview?.overview} />

          <CourseOutline sections={outline?.sections} />

          <CourseReviews reviews={reviews} distribution={distribution} />
        </div>

        <PurchasePanel
          contentId={contentId}
          imageUrl={imageUrl}
          title={details.displayName}
          modes={course.modes}
          modeDetails={course.modeDetails}
          isInCart={isInCart}
          isInWishlist={isInWishlist}
          isEnrolled={isEnrolled}
          enrolledMode={enrolledMode}
          certificateModes={(course.courseCertificates || []).map((c) => c.mode)}
          cartMode={cartItem?.mode}
          onToggleCart={handleToggleCart}
          onToggleWishlist={handleToggleWishlist}
          onChangeMode={handleChangeMode}
          onEnrollNow={handleEnrollNow}
          paymentsEnabled={paymentsEnabled}
          cartEnabled={cartEnabled}
          pendingTarget={pendingTarget}
          isNew={Boolean(isNewByCourseId[courseId])}
          subscriptionPlans={subscription.plans}
          isSubscribed={subscription.isSubscribed}
          subscriptionPending={subscription.redirecting}
          onSubscribe={subscription.subscribe}
          subscriptionOnly={course.subscriptionOnly}
          inviteOnly={course.inviteOnly}
        />
      </div>
    </div>
  );
};

export default CourseDetail;
